import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, map, mergeMap, withLatestFrom } from 'rxjs/operators';
import * as AiFunctionCallActions from './ai-function-call.actions';
import * as AuthenticationActions from 'libs/authentication/src/lib/state/authentication.actions';
import { selectActiveProjectId } from 'libs/project/src/lib/states/active-project-state/active-project.selectors';

import { NotificationService } from '@rappider/services';
import { Navigate } from 'libs/shared/src/lib/states/router/router.actions';

import { AiFunctionCall } from '@rappider/rappider-sdk';

/* Named import */
import { AiFunctionCallControllerService as ApiService } from '@rappider/rappider-sdk';
import { cloneDeep } from 'lodash';

export const navigatePathAfterCreatingInstance = '';
export const navigatePathAfterUpdatingInstance = '';

@Injectable()
export class AiFunctionCallEffects {
  constructor(
    private actions$: Actions,
    private apiService: ApiService,
    private store: Store<any>,
    private notificationService: NotificationService
  ) { }

  getAiFunctionCalls$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AiFunctionCallActions.GetAiFunctionCalls),
      withLatestFrom(
        this.store.select(selectActiveProjectId)
      ),
      mergeMap(([action, activeProjectId]) => {
        /* fail if there is no active project */
        if (!activeProjectId) {
          return [
            AiFunctionCallActions.GetAiFunctionCallsFailure()
          ];
        }
        /* set a filter */
        const filter = {
        };
        /* get the AiFunctionCalls */
        return this.apiService.find({ filter }).pipe(
          mergeMap(aiFunctionCalls => ([
            AiFunctionCallActions.GetAiFunctionCallsSuccessful({ payload: { aiFunctionCalls: aiFunctionCalls } })
          ])),
          catchError((error) => [
            AiFunctionCallActions.GetAiFunctionCallsFailure()
          ])
        );
      })
    )
  );

  createAiFunctionCall$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AiFunctionCallActions.CreateAiFunctionCall),
      withLatestFrom(
        this.store.select(selectActiveProjectId)
      ),
      mergeMap(([action, activeProjectId]) => {
        const params = {
          body: {
            ...action.payload.newEntity,
            projectId: activeProjectId
          }
        };

        const deleteFieldsIfEmpty = ['postWorkflowStepFunctionId', 'responseJsonSchema'];
        deleteFieldsIfEmpty.forEach(field => {
          if (params.body[field] === '' || params.body[field] === null) {
            delete params.body[field];
          }
        });

        return this.apiService.create$Json(params).pipe(
          map((aiFunctionCall) => {
            this.notificationService.createNotification(
              'success',
              params.body?.name,
              'SHARED.SUCCESSFULLY_CREATED'
            );
            return AiFunctionCallActions.CreateAiFunctionCallSuccessful({ payload: { newEntity: aiFunctionCall } });
          }),
          catchError((error) => {
            this.notificationService.createNotification(
              'error',
              params.body?.name,
              'SHARED.COULDNT_CREATED'
            );
            return [
              AiFunctionCallActions.CreateAiFunctionCallFailure(),
              AiFunctionCallActions.ErrorAction({ payload: { error: error, key: 'CreateAiFunctionCall', timeStamp: Date.now() } })
            ];
          })
        );
      })
    )
  );

  createAiFunctionCallSuccessful$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AiFunctionCallActions.CreateAiFunctionCallSuccessful),
      map(action =>
        new Navigate({ url: navigatePathAfterCreatingInstance })
      )
    )
  );

  updateAiFunctionCall$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AiFunctionCallActions.UpdateAiFunctionCall),
      mergeMap(action => {
        const params = cloneDeep({
          id: action.payload.id,
          body: action.payload.updatedData as any
        });

        const deleteFieldsIfEmpty = ['postWorkflowStepFunctionId', 'responseJsonSchema'];
        deleteFieldsIfEmpty.forEach(field => {
          if (params.body[field] === '' || params.body[field] === null) {
            delete params.body[field];
          }
        });

        return this.apiService.updateById(params).pipe(
          map((aiFunctionCall) => {
            this.notificationService.createNotification(
              'success',
              params.body.name,
              'SHARED.SUCCESSFULLY_UPDATED'
            );
            return AiFunctionCallActions.UpdateAiFunctionCallSuccessful({ payload: { id: action.payload.id, updatedData: aiFunctionCall } });
          }
          ),
          catchError((error) => {
            this.notificationService.createNotification(
              'error',
              params.body.name,
              'SHARED.COULDNT_UPDATED'
            );
            return [
              AiFunctionCallActions.UpdateAiFunctionCallFailure(),
              AiFunctionCallActions.ErrorAction({ payload: { error: error, key: 'UpdateAiFunctionCall', timeStamp: Date.now() } })
            ];
          })
        );
      })
    )
  );

  updateAiFunctionCallSuccessful$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AiFunctionCallActions.UpdateAiFunctionCallSuccessful),
      mergeMap(action => [
        new Navigate({ url: navigatePathAfterCreatingInstance }),
        AiFunctionCallActions.GetAiFunctionCalls()
      ])
    )
  );

  deleteAiFunctionCall$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AiFunctionCallActions.DeleteAiFunctionCall),
      withLatestFrom(
        this.store.select(state => <AiFunctionCall[]>state.aiFunctionCall?.data)
      ),
      mergeMap(([action, aiFunctionCalls]) => {
        const params = { id: action.payload.id };
        const deletedAiFunctionCall = aiFunctionCalls.find(aiFunctionCall => aiFunctionCall.id === action.payload.id);

        return this.apiService.deleteById(params).pipe(
          map(() => {
            this.notificationService.createNotification(
              'success',
              deletedAiFunctionCall?.title || 'AiFunctionCall',
              'SHARED.SUCCESSFULLY_DELETED'
            );
            return AiFunctionCallActions.GetAiFunctionCalls();
          }),
          catchError((error) => {
            this.notificationService.createNotification(
              'error',
              deletedAiFunctionCall.title || 'AiFunctionCall',
              'SHARED.COULDNT_DELETED'
            );
            return [
              AiFunctionCallActions.ErrorAction({ payload: { error: error, key: 'DeleteAiFunctionCall', timeStamp: Date.now() } })
            ];
          })
        );
      })
    )
  );

  /* Old actions can not listen on new reducers. So we use this effect temp. */
  resetStateToInitial$ = createEffect(() =>
    this.actions$.pipe(
      ofType<AuthenticationActions.Logout>(AuthenticationActions.ActionTypes.Logout),
      mergeMap(action => [
        AiFunctionCallActions.ResetStateToInitial()
      ])
    )
  );

  resetStateToInitialChangeProject$ = createEffect(() =>
    this.actions$.pipe(
      ofType<AuthenticationActions.UpdateAuthenticationTokenWithProjectIdSuccessful>(
        AuthenticationActions.ActionTypes.UpdateAuthenticationTokenWithProjectIdSuccessful),
      mergeMap(action => [
        AiFunctionCallActions.ResetStateToInitial()
      ])
    )
  );
}
